import React, { useEffect, useMemo, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: (errorCode: string) => any;
          "expired-callback"?: () => any;
          action?: string;
          cData?: string;
          theme?: "light" | "dark" | "auto";
          language?: string;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    // Turnstile 官方建议 explicit rendering 使用 ?render=explicit
    const src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

    // 已加载则直接 resolve
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Turnstile script load failed")));
      // 如果 script 已经加载完，window.turnstile 应该存在
      if (window.turnstile) resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile script load failed"));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export default function TurnstileWidget(props: {
  siteKey: string;
  onToken: (token: string) => void;
  action?: string;
}) {
  const { siteKey, onToken, action } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  const stableOnToken = useMemo(() => onToken, [onToken]);

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) return;

    loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        if (!window.turnstile) throw new Error("window.turnstile not found");
        if (!containerRef.current) return;

        // 清空旧 DOM
        containerRef.current.innerHTML = "";

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          callback: (token: string) => stableOnToken(token),
          "expired-callback": () => stableOnToken(""),
          "error-callback": () => {
            stableOnToken("");
            return true;
          },
        });

        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setReady(false);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, action, stableOnToken]);

  if (!siteKey) {
    return (
      <div className="card">
        <b>Turnstile 未配置</b>
        <div className="muted">
          你还没有设置 <code>VITE_TURNSTILE_SITEKEY</code>。你可以先留空用于试跑（mock 模式仍然可提交流程），
          但上线前必须补齐并做服务端校验。
        </div>
      </div>
    );
  }

  return (
    <div>
      <div ref={containerRef} />
      {!ready ? <div className="muted">Turnstile loading…</div> : null}
    </div>
  );
}
